<template>
  <div class="threejsContainer">
    <div class="containerTitle">1-18.后期处理<button @click="changeMe">图层切换</button></div>
    <div ref="container" class="containerBody"></div>
    
  </div>
</template>

<script>
/**
 * 备注：this.cube2.material.depthTest = false, 
 *      这是关键，设置为false后，关闭深夜测试，启用renderOrder，
 *      renderOrder越大，显示的渲染越靠后，显示越上层。
 * 
 * “图层切换”按钮负责切换深度测试
 */
import * as THREE from 'three';
import {generateScene} from '@/threejsEnv/scene.js';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import {genPerspectiveCamera} from '@/threejsEnv/camera.js';
import {genRenderer} from '@/threejsEnv/renderer.js';
import {RenderPass} from 'three/examples/jsm/postprocessing/RenderPass.js';
import {UnrealBloomPass} from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import {EffectComposer} from 'three/examples/jsm/postprocessing/EffectComposer.js';
import {ShaderPass} from 'three/examples/jsm/postprocessing/ShaderPass.js';
import {OutputPass} from 'three/examples/jsm/postprocessing/OutputPass.js';
 
export default {
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      currentMesh: null,
      showLayer1: false,
      bloomComposer: null,
      finalComposer: null,
      materials: {},
      cube1: null,
      cube2: null,
      u_time: 0,
      window: {
        width: 0,
        height: 0
      },
      animationId: null
    };
  },
  mounted() {
    //初始化环境
    this.initEnv();
    //创建网格对象
    this.createMesh();
    this.animate();
    // 监听窗口大小变化
    window.addEventListener('resize', this.onWindowResize);
  },
  methods: {
    changeMe(){
      this.showLayer1 = !this.showLayer1;
    },
    createMesh() {
      
      const geometry1 = new THREE.BoxGeometry();
      const material1 = new THREE.MeshBasicMaterial({ color: 0x00ff00});
      
      this.cube1 = new THREE.Mesh(geometry1, material1);
      this.cube1.position.x = 0.5;
      this.cube1.renderOrder = 1; 
      this.scene.add(this.cube1);
      
      const geometry2 = new THREE.BoxGeometry(0.3,0.3,0.3);
      const material2 = new THREE.MeshBasicMaterial({ 
        color: 0xff0000,
        emissive: 0xff0000,     // 自发光颜色（需与基础色一致或更亮）
        emissiveIntensity: 1    // 自发光强度（数值越大越亮，建议 3-10）
      });
      this.cube2 = new THREE.Mesh(geometry2, material2);
      this.cube2.position.x = -0.15;
      this.cube2.renderOrder = 10; 
      // 添加发光标记
      this.cube2.userData.isBloom = true; 
      this.scene.add(this.cube2);
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      this.controls.update();
      if(this.showLayer1){
        this.cube2.material.depthTest = false;
      }else{
        this.cube2.material.depthTest = true;
      }
      // 1. 只渲染需要bloom的物体
      this.scene.traverse(this.darkenNonBloomed);
      this.bloomComposer.render();
      
      // 2. 恢复所有物体材质
      this.scene.traverse(this.restoreMaterial);
      
      // 3. 渲染最终画面
      this.finalComposer.render();
    },
    initEnv(){
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
      // 初始化场景
      this.scene = generateScene();
      this.scene.background = new THREE.Color(0x000000);

      // 初始化相机
      this.camera = genPerspectiveCamera(this.window);

      // 初始化渲染器
      this.renderer = genRenderer(this.window);
      this.$refs.container.appendChild(this.renderer.domElement);

      // 添加轨道控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;

      // 创建光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.scene.add(ambientLight);
      
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 20, 15);
      this.scene.add(directionalLight);

      const gridHelper = new THREE.GridHelper(100, 100,
                                              0x444444,   // 中心轴线颜色（红色）
                                              0x222222    // 普通网格线颜色（绿色）
                                              ); // 10x10 网格
      this.scene.add(gridHelper);

      // bloom 效果参数配置
			const params = {
				threshold: 0.1,    // 亮度阈值，高于此值的像素才会产生 bloom 效果
				strength: 2,     // bloom 效果强度
				radius: 0.1,     // bloom 效果扩散半径
				exposure: 1      // 曝光度
			};
      // 创建基础渲染通道（渲染整个场景）
      const renderScene = new RenderPass( this.scene, this.camera );
      // 创建 UnrealBloom 效果通道
			const bloomPass = new UnrealBloomPass( 
				new THREE.Vector2( window.innerWidth, window.innerHeight ),  // 渲染尺寸
				3,  // 强度
				0.1,  // 阈值
				0.85  // 半径
			);
			// 应用参数配置
			bloomPass.threshold = params.threshold;
			bloomPass.strength = params.strength;
			bloomPass.radius = params.radius;
      
      // 创建 bloom 效果专用的合成器
			this.bloomComposer = new EffectComposer( this.renderer );
			this.bloomComposer.renderToScreen = false;  // 不直接渲染到屏幕
			this.bloomComposer.addPass( renderScene );  // 添加基础渲染通道
			this.bloomComposer.addPass( bloomPass );    // 添加 bloom 效果通道

      const mixPass = new ShaderPass(
				new THREE.ShaderMaterial( {
					uniforms: {
						baseTexture: { value: null },  // 基础场景纹理
						bloomTexture: { value: this.bloomComposer.renderTarget2.texture }  // bloom 效果纹理
					},
					vertexShader: `varying vec2 vUv;
                          void main() {
                            vUv = uv;
                            gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                        }`,  
					fragmentShader: `uniform sampler2D baseTexture;
                            uniform sampler2D bloomTexture;
                            varying vec2 vUv;
                            void main() {
                              // 混合基础纹理和 bloom 纹理
                              gl_FragColor = ( texture2D( baseTexture, vUv ) + texture2D( bloomTexture, vUv ) );
                          }`,  
					defines: {}
				} ), 
				'baseTexture'  // 主纹理输入
			);
			mixPass.needsSwap = true;  // 需要交换缓冲区

      // 创建输出通道，处理最终色彩空间转换
			const outputPass = new OutputPass();

      // 创建最终合成器，用于输出最终画面
      this.finalComposer = new EffectComposer( this.renderer );
      this.finalComposer.addPass( renderScene );  // 添加基础渲染
      this.finalComposer.addPass( mixPass );      // 添加混合效果
      this.finalComposer.addPass( outputPass );   // 添加输出处理

    },
    // 保存原始材质并替换为黑色材质（不参与bloom）
    darkenNonBloomed(obj) {
      if (obj.isMesh && !obj.userData.isBloom) {
        this.materials[obj.uuid] = obj.material;
        obj.material = new THREE.MeshBasicMaterial({ color: 0x000000 });
      }
    },
    // 恢复原始材质
    restoreMaterial(obj) {
      if (obj.isMesh && this.materials[obj.uuid]) {
        obj.material = this.materials[obj.uuid];
        delete this.materials[obj.uuid];
      }
    },
    onWindowResize() {
      const width = this.$refs.container.clientWidth;
      const height = this.$refs.container.clientHeight;

      // 更新相机
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();

      // 更新渲染器
      this.renderer.setSize(width, height);
    }
  },
  beforeDestroy() {
    // 清理动画和事件监听
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.onWindowResize);
    
    // 释放资源
    if (this.currentMesh) {
      this.scene.remove(this.currentMesh);
      this.currentMesh.geometry.dispose();
      this.currentMesh.material.dispose();
    }
    
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
};
</script>

<style>
</style>
